/**
 * 首页「待审批发券」卡片（超管专属）。
 *
 * 运营主管（ops_lead）提交的点名券申请要超管审批后才会真正发出；超管打开首页就该一眼看到有没有待办。
 * 仅持 voucher:approve 的角色渲染，且只有此时才请求接口（其余角色不渲染、不发请求）。
 * 待审批数 > 0 时高亮；另有「结果不明」的发券（需要人工到免单券列表核对）时单独红字提示。
 * 点击整张卡进「发券申请」页。
 */
import { Link } from 'react-router-dom';
import { Card, Statistic, Typography, theme } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../contracts/permissions';
import { getVoucherGrantPendingCount } from '../../api/voucher-grant-requests';

const { Text } = Typography;

export function VoucherApprovalsCard() {
  const canApprove = usePermission(PERMISSIONS.VOUCHER_APPROVE);
  const { token } = theme.useToken();
  const query = useQuery({
    // 以 voucher-grant-requests 为前缀：发券申请页里任何写操作的失效都会顺带刷新这张卡
    queryKey: ['voucher-grant-requests', 'pending-count'],
    queryFn: getVoucherGrantPendingCount,
    enabled: canApprove,
    retry: false,
    refetchInterval: 60_000,
  });
  if (!canApprove) return null;

  const count = query.data?.count ?? 0;
  const unknownCount = query.data?.unknownCount ?? 0;
  const highlight = count > 0 || unknownCount > 0;

  return (
    <div style={{ marginTop: 16 }} data-testid="voucher-approvals-card" data-highlight={highlight ? 'true' : 'false'}>
      <Link to="/vouchers/requests">
        <Card
          hoverable
          size="small"
          loading={query.isLoading}
          style={highlight ? { borderColor: token.colorWarning, background: token.colorWarningBg } : undefined}
        >
          <Statistic
            title="待审批发券"
            value={count}
            valueStyle={count > 0 ? { color: token.colorWarning } : undefined}
            formatter={(v) => <span data-testid="voucher-approvals-count">{v}</span>}
          />
          {unknownCount > 0 && (
            <div>
              <Text type="danger" data-testid="voucher-approvals-unknown">
                另有 {unknownCount} 笔发券结果不明，需要人工核对
              </Text>
            </div>
          )}
          {query.isError && <Text type="secondary">待审批数量暂时加载失败，点击进入发券申请页查看。</Text>}
        </Card>
      </Link>
    </div>
  );
}
